/**
 * Ubezpieczenia — teksty obszaru `policies` (polisy, umowy, import, ochrona pojazdów) pod `ins.policies.*`.
 * WŁAŚCICIEL: obszar `policies`. Podpięte w i18n/insurance.ts. `en` typowane względem `pl`.
 */
type Shape<T> = { [K in keyof T]: T[K] extends string ? string : Shape<T[K]> };

export const insPoliciesPl = {
  title: 'Polisy i umowy',
  description: 'Portfel polis floty: umowy generalne, polisy pojazdów, ochrona i końce okresów — z importu plików od ubezpieczyciela lub brokera',
  loadFailed: 'Nie udało się wczytać danych',
  exported: 'Pobrano plik Excela',
  periodElapsed: 'Upłynęło {pct}% okresu ochrony',
  yes: 'tak',
  no: 'nie',
  actions: { add: 'Dodaj polisę', import: 'Import polis', export: 'Eksport do Excela', edit: 'Edytuj', delete: 'Usuń', save: 'Zapisz', cancel: 'Anuluj', close: 'Zamknij', retry: 'Spróbuj ponownie', clearFilters: 'Wyczyść filtry' },
  kpi: {
    policies: 'Polisy w portfelu',
    policiesHint: '{vehicles} pojazdów · {contracts} umów',
    premium: 'Składka roczna',
    premiumHint: 'ubezpieczyciele: {n}',
    covered: 'Pojazdy chronione',
    coveredHint: '{pct}% z {total} aktywnych pojazdów',
    gaps: 'Bez ochrony OC',
    gapsHint: 'w tym bez OC już dziś: {n}',
    registryEmpty: 'rejestr polis jest pusty',
    nearestEnd: 'Najbliższy koniec',
  },
  insight: {
    gapsToday: 'Pojazdy bez ważnego OC dziś: {n}. Pojazd bez OC nie powinien wyjechać w trasę.',
    showGaps: 'Pokaż luki',
    expiring: 'W ciągu {days} dni kończy się polis: {n} — czas na odnowienie.',
    showExpiring: 'Pokaż kończące się',
  },
  views: { label: 'Widok portfela', list: 'Lista', insurer: 'Wg ubezpieczyciela', type: 'Wg typu pojazdu', timeline: 'Kalendarz', coverage: 'Ochrona pojazdów', contracts: 'Umowy generalne' },
  tabs: { label: 'Zakładki polis', all: 'Wszystkie', oc: 'Tylko OC', ocac: 'OC + AC', cargo: 'Cargo / OCP', expiring: 'Kończące się ≤ {days} dni', novehicle: 'Bez pojazdu we flocie' },
  filters: {
    toggle: 'Filtry',
    search: 'Szukaj: nr rej., nr polisy, VIN, model, ubezpieczyciel…',
    insurer: 'Ubezpieczyciel',
    allInsurers: 'Każdy ubezpieczyciel',
    line: 'Ryzyko',
    allLines: 'Każde ryzyko',
    type: 'Typ pojazdu',
    allTypes: 'Każdy typ pojazdu',
    contract: 'Umowa',
    allContracts: 'Każda umowa',
    noContract: 'Bez umowy generalnej',
    status: 'Okres',
    statusActive: 'Obowiązujące i przyszłe',
    statusExpired: 'Tylko wygasłe',
    statusAll: 'Wszystkie lata',
  },
  cols: { plate: 'Nr rej.', vehicleLabel: 'Pojazd', policyNumber: 'Nr polisy', insurerName: 'Ubezpieczyciel', lines: 'Zakres', acSumInsured: 'Suma AC', premiumTotal: 'Składka', endDate: 'Koniec', claims12m: 'Szkody 12 m.' },
  lineShort: { ASS: 'ASS', SZYBY: 'Szyby', CARGO: 'Cargo', OCP: 'OCP' },
  state: { valid: 'Ważna · {n} dni', endsIn: 'Kończy się za {n} dni', endsToday: 'Kończy się dziś', expired: 'Wygasła', future: 'Od {date}' },
  list: {
    shown: 'Polisy w widoku: {n}',
    premiumInView: 'składka',
    page: 'strona {page} z {pages}',
    prev: 'Poprzednia strona',
    next: 'Następna strona',
    noVehicle: 'Brak pojazdu we flocie',
    leasing: 'w leasingu',
    emptyFiltered: 'Brak polis dla tych filtrów',
    emptyFilteredHint: 'Zmień zakładkę albo zdejmij filtry.',
    emptyRegistry: 'Rejestr polis jest pusty',
    emptyRegistryHint: 'Polisy trafiają tu z pliku od ubezpieczyciela lub brokera (Excel) albo z ręcznego dodania polisy.',
  },
  groups: {
    byInsurer: 'Składka wg ubezpieczyciela',
    byType: 'Składka wg typu pojazdu',
    chartHint: 'Udział w składce portfela w bieżących filtrach',
    premium: 'składka',
    counts: 'polisy: {policies} · pojazdy: {vehicles}',
    share: '{pct}% składki',
    expiring: 'kończące się: {n}',
    acSum: 'suma AC {amount}',
    emptyHint: 'Zmień filtry albo zaimportuj polisy.',
  },
  timeline: {
    title: 'Terminy',
    chartTitle: 'Końce ochrony i raty w miesiącach',
    chartHint: 'Słupki — liczba polis kończących się w miesiącu; linia — raty do zapłaty',
    ends: 'Końce okresu',
    due: 'Raty do zapłaty',
    kindAll: 'Wszystko',
    kindEnds: 'Okresy polis',
    kindInstallments: 'Raty',
    empty: 'Brak terminów w najbliższych miesiącach',
    monthEnds: 'końce okresu: {n}',
    monthDue: 'do zapłaty {amount}',
    endRow: 'Koniec okresu ubezpieczenia — polisy: {n}',
    startRow: 'Początek nowego okresu — polisy: {n}',
    installmentContract: 'Rata — umowa {number}',
    installmentLoose: 'Raty polis spoza umów generalnych ({n})',
    paid: 'zapłacona',
    unpaid: 'do zapłaty',
    overdue: 'po terminie',
  },
  coverage: {
    title: 'Ochrona pojazdów',
    search: 'Szukaj pojazdu…',
    kpiCovered: 'Flota chroniona',
    kpiCoveredHint: 'pojazdy z OC dziś i jutro: {n} z {total}',
    kpiGapsToday: 'Bez OC dziś',
    kpiGapsTodayHint: 'alarm — pojazd nie powinien wyjechać',
    kpiGapsTomorrow: 'Bez OC od jutra',
    kpiGapsTomorrowHint: 'w tym ochrona kończąca się dziś',
    kpiExpiring: 'Kończy się ≤ {days} dni',
    kpiExpiringHint: 'odnów, zanim powstanie luka',
    byType: 'pojazdy: {n} · bez OC: {gaps}',
    filter: { all: 'Wszystkie', gaps: 'Luki ochrony', expiring: 'Kończące się', ok: 'Chronione' },
    reason: { NO_POLICY: 'Brak polisy OC', EXPIRED: 'Polisa OC wygasła', NOT_STARTED: 'Polisa OC jeszcze nie działa', MISSING_LINE: 'Polisa bez OC', EXPIRES_TODAY: 'OC kończy się dziś' },
    coveredUntil: 'OC do {date}',
    lastPolicy: 'Ostatnia polisa {number} skończyła się {date}',
    nextPolicy: 'Następna polisa {number} od {date}',
    openPolicy: 'Karta polisy',
    registryEmpty: 'Rejestr polis jest pusty — luki pokażą się jako alarm po pierwszym imporcie polis.',
    noGaps: 'Każdy aktywny pojazd ma dziś i jutro ważne OC',
    empty: 'Brak pojazdów dla tego filtra',
  },
  contracts: {
    add: 'Dodaj umowę',
    addTitle: 'Nowa umowa generalna',
    editTitle: 'Edycja umowy generalnej',
    empty: 'Brak umów generalnych',
    emptyHint: 'Umowa powstaje z importu pliku z numerem umowy albo z ręcznego dodania.',
    broker: 'broker',
    daysLeft: 'jeszcze {n} dni',
    showPolicies: 'Polisy ({n})',
    today: 'Dziś',
    policies: 'Polisy',
    vehicles: 'pojazdy: {n}',
    premium: 'Składka',
    fromPolicies: 'z polis {amount}',
    paid: 'Zapłacono',
    due: 'Do zapłaty',
    next: 'następna {date}',
    ocOnly: 'Tylko OC: {n}',
    ocAc: 'OC + AC: {n}',
    cargo: 'Cargo / OCP: {n}',
    endDate: 'koniec {date}: {n}',
    installments: 'Raty',
    noInstallments: 'Brak rat w umowie.',
    paidOn: 'zapłacona {date}',
    inDays: 'za {n} dni',
    markPaid: 'Zapłacona',
    undoPaid: 'Cofnij',
    markedPaid: 'Rata oznaczona jako zapłacona',
    markedUnpaid: 'Cofnięto zapłatę raty',
    confirmDelete: 'Usunąć umowę {number}? Polisy zostaną w rejestrze bez umowy.',
    deleted: 'Umowa usunięta',
    saved: 'Umowa zapisana',
    addInstallment: 'Dodaj ratę',
    installmentsHint: 'Raty polis przypisanych do umowy są sumowane automatycznie po każdym imporcie.',
    fields: { number: 'Numer umowy', insurer: 'Ubezpieczyciel', broker: 'Broker', title: 'Nazwa', start: 'Początek', end: 'Koniec', totalPremium: 'Składka łączna wg umowy (zł)', notes: 'Notatki', dueDate: 'Termin raty', amount: 'Kwota raty' },
  },
  drawer: {
    policy: 'Polisa',
    aria: 'Polisa {number}',
    period: 'Okres ochrony',
    premium: 'Składka',
    acSum: 'Suma AC',
    claims12m: 'Szkody 12 mies.',
    contract: 'Umowa generalna',
    vehicle: 'Pojazd',
    noVehicle: 'Pojazdu z polisy nie ma we flocie — dodaj go albo wskaż pojazd w edycji polisy.',
    scope: 'Zakres i sumy',
    premiums: 'Składki i raty',
    installment: 'Rata {n} · {date}',
    claims: 'Szkody pojazdu',
    claimsPeriod: 'W okresie polisy',
    claimsPaid: 'wypłaty {amount} · {pct}% składki',
    downtime: 'przestój: {n} dni',
    history: 'Historia polis pojazdu',
    source: 'Źródło',
    importFile: 'Plik importu',
    rawRow: 'Wiersz z pliku',
    confirmDelete: 'Usunąć polisę {number} z rejestru?',
    deleted: 'Polisa usunięta',
  },
  form: { addTitle: 'Nowa polisa', editTitle: 'Edycja polisy', addHint: 'Polisa indywidualna spoza importu (np. dokupiona w trakcie roku).', noVehicle: 'Pojazd spoza floty', saved: 'Polisa zapisana' },
  documents: { title: 'Dokumenty', empty: 'Brak dokumentów (polisa PDF, OWU, aneks).', upload: 'Dodaj plik', uploaded: 'Dodano dokument', download: 'Pobierz', confirmDelete: 'Usunąć plik {name}?' },
  fields: {
    policyNumber: 'Nr polisy', insurerName: 'Ubezpieczyciel', startDate: 'Początek okresu', endDate: 'Koniec okresu', registrationNo: 'Nr rejestracyjny', vin: 'VIN',
    vehicleLabel: 'Opis pojazdu', productionYear: 'Rok produkcji', objectKind: 'Typ pojazdu', ownerName: 'Właściciel', ownerNip: 'NIP właściciela', leasing: 'Leasing',
    lines: 'Ryzyka', ocSumPerson: 'OC — suma (osoby)', ocSumProperty: 'OC — suma (mienie)', acSumInsured: 'Suma AC (zł)', acSumBasis: 'Podstawa sumy AC',
    acVariant: 'Wariant AC', acTerritory: 'Zakres terytorialny AC', acDeductible: 'Franszyza AC (zł)', acOwnShare: 'Udział własny AC', premiumTotal: 'Składka łączna (zł)',
    premiumByLine: 'Składki za ryzyka', installments: 'Raty', coverage: 'Zakres dodatkowy', vehicleId: 'Pojazd floty', contractId: 'Umowa generalna',
  },
  coverageKeys: {
    acValue100: 'AC — wartość 100%', acSumProtection: 'AC — ochrona sumy', acTowingParking: 'Holowanie i parking', acTowingLimit: 'Holowanie — limit',
    acReplacementLimit: 'Auto zastępcze — limit', acNonStop: 'Auto Non Stop', acRussiaBelarus: 'Kradzież: Rosja, Białoruś, Ukraina, Mołdawia', nnwSum: 'NNW — suma',
    cargoSum: 'Cargo — suma ubezpieczenia', pwdVariant: 'Pomoc w drodze — wariant', zkSumPerson: 'Zielona Karta — suma (osoby)', zkSumProperty: 'Zielona Karta — suma (mienie)',
    capacity: 'Pojemność [cm³]', dmc: 'DMC', ZK: 'Zielona Karta',
  },
  import: {
    title: 'Import polis',
    description: 'Plik Excela od ubezpieczyciela lub brokera → podgląd zmian w rejestrze i dopasowanie do floty → zatwierdzenie',
    stepsAria: 'Kroki importu',
    step: 'Krok {n}',
    steps: { upload: 'Wgraj plik', preview: 'Sprawdź zmiany', done: 'Gotowe' },
    uploadTitle: 'Plik od ubezpieczyciela lub brokera',
    uploadHint: 'Excel .xlsx — załącznik do umowy flotowej albo zestawienie polis. Stare pliki .xls zapisz jako .xlsx.',
    drop: 'Przeciągnij tu plik albo wybierz go z dysku',
    choose: 'Wybierz plik',
    insurerLabel: 'Ubezpieczyciel (gdy plik go nie podaje)',
    insurerPlaceholder: 'np. nazwa towarzystwa',
    insurerHint: 'Zestawienia brokera mają ubezpieczyciela w wierszu — wtedy zostaw puste.',
    previewBtn: 'Pokaż podgląd',
    tips: {
      layouts: 'Układ kolumn rozpoznajemy po nagłówkach: flagi TAK/NIE, zakres AC, ryzyka w jednej kolumnie.',
      safe: 'Podgląd niczego nie zapisuje — rejestr zmienia się dopiero po zatwierdzeniu.',
      vehicles: 'Polisy łączymy z pojazdami floty po numerze rejestracyjnym, a gdy go brak — po VIN.',
    },
    another: 'Importuj kolejny plik',
    meta: '{layout} · pozycje: {rows} · kolumny: {columns} · arkusz „{sheet}”',
    layout: { FLEET_EXTENDED: 'Załącznik flotowy (rozszerzony)', FLEET_OC_AC: 'Załącznik flotowy OC + AC', FLEET_OC: 'Załącznik flotowy OC', BROKER_LIST: 'Zestawienie brokera', GENERIC: 'Inny układ' },
    status: { PREVIEW: 'Podgląd', APPLIED: 'Zatwierdzony', DISCARDED: 'Odrzucony' },
    rowStatus: { created: 'Nowa', updated: 'Zmieniona', unchanged: 'Bez zmian' },
    sum: {
      created: 'Nowe polisy',
      updated: 'Zmienione',
      unchanged: 'Bez zmian',
      removed: 'brak w pliku: {n}',
      premium: 'Składka w pliku',
      acSum: 'suma AC {amount}',
      matched: 'dopasowane do floty: {n}',
      unmatched: 'bez pojazdu we flocie: {n}',
      withoutPolicy: 'pojazdy floty bez polisy: {n}',
      corrections: 'ujednolicenia: {n}',
    },
    noInsurer: 'Brak ubezpieczyciela — wpisz go przy wgrywaniu pliku',
    hasErrors: 'Plik ma błędy: {n}. Pozycje z błędną datą nie dadzą się zatwierdzić — popraw plik i wgraj go ponownie.',
    apply: 'Zatwierdź import',
    discard: 'Odrzuć',
    confirmApply: 'Zatwierdzić import {n} pozycji? Polisy zostaną zapisane w rejestrze, a terminy ubezpieczenia w pojazdach floty zaktualizowane.',
    appliedTitle: 'Import zatwierdzony',
    appliedSummary: 'Nowe: {created} · zmienione: {updated} · bez zmian: {unchanged} · zaktualizowane pojazdy: {vehicles}',
    discarded: 'Import odrzucony',
    discardedTitle: 'Import odrzucony — rejestr bez zmian',
    toPolicies: 'Przejdź do polis',
    warningsTitle: 'Ostrzeżenia i propozycje',
    warningsHint: 'Rozwiń, żeby zobaczyć wiersze. Ujednolicenia zapisujemy, oryginał zostaje w historii wiersza.',
    rowsAll: 'Wszystkie pozycje',
    onlyUnmatched: 'Tylko bez pojazdu',
    moreRows: 'Pozostałe pozycje: {n} (zobaczysz je po zatwierdzeniu na liście polis)',
    rowNo: 'wiersz {n}',
    history: 'Historia importów',
    historyEmpty: 'Nie było jeszcze importów.',
    historyCounts: 'pozycje: {rows} · nowe: {created} · zmienione: {updated}',
    warning: {
      MODEL_NO_BRAND: { title: 'Model bez marki', hint: 'Dopisujemy markę z nazwy modelu (np. Sprinter → Mercedes).' },
      NUMERIC_DATE: { title: 'Data zapisana jako liczba', hint: 'Odczytujemy ją jako datę Excela.' },
      EMPTY_COLUMN: { title: 'Puste kolumny', hint: 'Kolumna z nagłówkiem, ale bez wartości.' },
      BAD_AMOUNT: { title: 'Nieczytelne kwoty', hint: 'Kwota nie jest liczbą — pole zostanie puste.' },
      BAD_DATE: { title: 'Nieczytelna data okresu', hint: 'Bez dat okresu import nie da się zatwierdzić.' },
      MISSING_REGISTRATION: { title: 'Brak numeru rejestracyjnego', hint: 'Dopasujemy pojazd po VIN, jeśli jest.' },
      DUPLICATE_POLICY: { title: 'Powtórzona polisa', hint: 'Ten sam numer i okres występuje w pliku kilka razy — zapisze się ostatni wiersz.' },
      UNKNOWN_COLUMNS: { title: 'Nierozpoznane kolumny', hint: 'Zostają w historii wiersza polisy.' },
      TOTAL_MISMATCH: { title: 'Składka łączna ≠ suma składek', hint: 'Zapisujemy składkę łączną z pliku.' },
      NO_LINES: { title: 'Brak ryzyk', hint: 'Nie rozpoznano żadnego ryzyka — sprawdź kolumny zakresu.' },
      NO_VEHICLE: { title: 'Pojazdu nie ma we flocie', hint: 'Polisa zapisze się bez pojazdu — dodaj pojazd do floty albo wskaż go później.' },
      MATCHED_BY_VIN: { title: 'Dopasowano po VIN', hint: 'Numer rejestracyjny we flocie jest inny niż w polisie.' },
      VIN_MISMATCH: { title: 'Inny VIN niż we flocie', hint: 'Sprawdź, czy to ten sam pojazd.' },
      VEHICLE_WITHOUT_POLICY: { title: 'Pojazdy floty bez polisy', hint: 'Aktywne pojazdy tego typu bez OC w rejestrze ani w pliku — alarm.' },
      MISSING_IN_FILE: { title: 'Brak w pliku', hint: 'Polisy tej umowy z rejestru, których nie ma w pliku — zostają bez zmian.' },
    },
  },
  vehicleCard: {
    tab: 'Ubezpieczenie',
    title: 'Ochrona pojazdu',
    covered: 'Ważne OC dziś i jutro',
    noCoverToday: 'Brak ważnego OC dziś',
    endsToday: 'Ochrona kończy się dziś',
    required: 'Wymagane: OC komunikacyjne',
    openPolicy: 'Karta polisy',
    insuranceDue: 'Termin we flocie',
    upcoming: 'Następna polisa {number} ({insurer}) od {date}',
    noPolicy: 'Brak polisy tego pojazdu w rejestrze',
    noPolicyHint: 'Zaimportuj plik od ubezpieczyciela albo dodaj polisę ręcznie.',
    claims: 'Szkody pojazdu',
    claims12m: 'Szkody 12 mies.',
    paid12m: 'Wypłaty 12 mies.',
    lossRatio: '{pct}% składki',
  },
  errors: {
    network: 'Brak połączenia z serwerem.',
    forbidden: 'Brak uprawnień do tej operacji.',
    generic: 'Coś poszło nie tak. Spróbuj ponownie.',
    POLICY_NOT_FOUND: 'Nie znaleziono polisy.',
    POLICY_VEHICLE_NOT_FOUND: 'Nie znaleziono pojazdu we flocie.',
    POLICY_REQUIRED_FIELDS: 'Podaj numer polisy, ubezpieczyciela, okres i co najmniej jedno ryzyko.',
    POLICY_BAD_PERIOD: 'Koniec okresu jest przed początkiem.',
    POLICY_DUPLICATE: 'Polisa o tym numerze i początku okresu już jest w rejestrze.',
    CONTRACT_NOT_FOUND: 'Nie znaleziono umowy.',
    CONTRACT_REQUIRED_FIELDS: 'Podaj numer umowy, ubezpieczyciela i okres.',
    CONTRACT_BAD_PERIOD: 'Koniec umowy jest przed początkiem.',
    CONTRACT_DUPLICATE: 'Umowa o tym numerze już istnieje.',
    INSTALLMENT_NOT_FOUND: 'Nie ma takiej raty.',
    VEHICLE_NOT_FOUND: 'Nie znaleziono pojazdu.',
    IMPORT_FILE_REQUIRED: 'Wybierz plik.',
    IMPORT_XLS_UNSUPPORTED: 'Stary format .xls — zapisz plik w Excelu jako .xlsx i wgraj ponownie.',
    IMPORT_NOT_EXCEL: 'To nie jest plik Excela .xlsx.',
    IMPORT_NO_SHEET: 'Skoroszyt nie ma żadnego arkusza.',
    IMPORT_LAYOUT_UNKNOWN: 'Nie rozpoznano układu: brak wiersza nagłówka z kolumną „Nr polisy”.',
    IMPORT_MISSING_COLUMNS: 'Brak kolumn: nr polisy, nr rejestracyjny / VIN albo okres ubezpieczenia (od / do).',
    IMPORT_NO_ROWS: 'Plik nie zawiera pozycji z numerem polisy.',
    IMPORT_NOT_FOUND: 'Nie znaleziono importu.',
    IMPORT_ALREADY_DECIDED: 'Ten import został już zatwierdzony albo odrzucony.',
    IMPORT_BAD_PERIODS: 'Część pozycji nie ma czytelnego okresu ubezpieczenia — popraw plik i wgraj go ponownie.',
    IMPORT_INSURER_REQUIRED: 'Brak ubezpieczyciela — wgraj plik ponownie i wpisz ubezpieczyciela.',
    ATTACHMENT_OWNER_NOT_FOUND: 'Nie znaleziono dokumentu.',
    FILE_REQUIRED: 'Wybierz plik.',
    FILE_TYPE_NOT_ALLOWED: 'Niedozwolony typ pliku.',
  },
};

export const insPoliciesEn: Shape<typeof insPoliciesPl> = {
  title: 'Policies & contracts',
  description: 'Fleet insurance portfolio: master contracts, vehicle policies, coverage and renewals — imported from insurer or broker files',
  loadFailed: 'Could not load the data',
  exported: 'Excel file downloaded',
  periodElapsed: '{pct}% of the coverage period elapsed',
  yes: 'yes',
  no: 'no',
  actions: { add: 'Add policy', import: 'Import policies', export: 'Export to Excel', edit: 'Edit', delete: 'Delete', save: 'Save', cancel: 'Cancel', close: 'Close', retry: 'Try again', clearFilters: 'Clear filters' },
  kpi: {
    policies: 'Policies in portfolio',
    policiesHint: '{vehicles} vehicles · {contracts} contracts',
    premium: 'Annual premium',
    premiumHint: 'insurers: {n}',
    covered: 'Covered vehicles',
    coveredHint: '{pct}% of {total} active vehicles',
    gaps: 'Without MTPL',
    gapsHint: 'already without MTPL today: {n}',
    registryEmpty: 'the policy register is empty',
    nearestEnd: 'Next expiry',
  },
  insight: {
    gapsToday: 'Vehicles without valid MTPL today: {n}. A vehicle without MTPL should not go on a route.',
    showGaps: 'Show gaps',
    expiring: '{n} policies expire within {days} days — time to renew.',
    showExpiring: 'Show expiring',
  },
  views: { label: 'Portfolio view', list: 'List', insurer: 'By insurer', type: 'By vehicle type', timeline: 'Calendar', coverage: 'Vehicle coverage', contracts: 'Master contracts' },
  tabs: { label: 'Policy tabs', all: 'All', oc: 'MTPL only', ocac: 'MTPL + own damage', cargo: 'Cargo / carrier', expiring: 'Expiring ≤ {days} days', novehicle: 'No fleet vehicle' },
  filters: {
    toggle: 'Filters',
    search: 'Search: plate, policy no., VIN, model, insurer…',
    insurer: 'Insurer',
    allInsurers: 'Any insurer',
    line: 'Line',
    allLines: 'Any line',
    type: 'Vehicle type',
    allTypes: 'Any vehicle type',
    contract: 'Contract',
    allContracts: 'Any contract',
    noContract: 'No master contract',
    status: 'Period',
    statusActive: 'Current and future',
    statusExpired: 'Expired only',
    statusAll: 'All years',
  },
  cols: { plate: 'Plate', vehicleLabel: 'Vehicle', policyNumber: 'Policy no.', insurerName: 'Insurer', lines: 'Cover', acSumInsured: 'Own damage sum', premiumTotal: 'Premium', endDate: 'Ends', claims12m: 'Claims 12 m.' },
  lineShort: { ASS: 'ASS', SZYBY: 'Glass', CARGO: 'Cargo', OCP: 'Carrier' },
  state: { valid: 'Valid · {n} days', endsIn: 'Ends in {n} days', endsToday: 'Ends today', expired: 'Expired', future: 'From {date}' },
  list: {
    shown: 'Policies in view: {n}',
    premiumInView: 'premium',
    page: 'page {page} of {pages}',
    prev: 'Previous page',
    next: 'Next page',
    noVehicle: 'No vehicle in the fleet',
    leasing: 'leased',
    emptyFiltered: 'No policies match these filters',
    emptyFilteredHint: 'Change the tab or clear the filters.',
    emptyRegistry: 'The policy register is empty',
    emptyRegistryHint: 'Policies come from an insurer or broker Excel file, or can be added manually.',
  },
  groups: {
    byInsurer: 'Premium by insurer',
    byType: 'Premium by vehicle type',
    chartHint: 'Share of portfolio premium with the current filters',
    premium: 'premium',
    counts: 'policies: {policies} · vehicles: {vehicles}',
    share: '{pct}% of premium',
    expiring: 'expiring: {n}',
    acSum: 'own damage sum {amount}',
    emptyHint: 'Change the filters or import policies.',
  },
  timeline: {
    title: 'Deadlines',
    chartTitle: 'Expiries and installments by month',
    chartHint: 'Bars — policies expiring in the month; line — installments due',
    ends: 'Expiries',
    due: 'Installments due',
    kindAll: 'Everything',
    kindEnds: 'Policy periods',
    kindInstallments: 'Installments',
    empty: 'No deadlines in the coming months',
    monthEnds: 'expiries: {n}',
    monthDue: 'due {amount}',
    endRow: 'Coverage period ends — policies: {n}',
    startRow: 'New period starts — policies: {n}',
    installmentContract: 'Installment — contract {number}',
    installmentLoose: 'Installments of policies outside contracts ({n})',
    paid: 'paid',
    unpaid: 'due',
    overdue: 'overdue',
  },
  coverage: {
    title: 'Vehicle coverage',
    search: 'Search vehicle…',
    kpiCovered: 'Fleet covered',
    kpiCoveredHint: 'vehicles with MTPL today and tomorrow: {n} of {total}',
    kpiGapsToday: 'No MTPL today',
    kpiGapsTodayHint: 'alarm — the vehicle should not be driven',
    kpiGapsTomorrow: 'No MTPL from tomorrow',
    kpiGapsTomorrowHint: 'including cover ending today',
    kpiExpiring: 'Ends ≤ {days} days',
    kpiExpiringHint: 'renew before a gap appears',
    byType: 'vehicles: {n} · without MTPL: {gaps}',
    filter: { all: 'All', gaps: 'Coverage gaps', expiring: 'Expiring', ok: 'Covered' },
    reason: { NO_POLICY: 'No MTPL policy', EXPIRED: 'MTPL policy expired', NOT_STARTED: 'MTPL policy not started yet', MISSING_LINE: 'Policy without MTPL', EXPIRES_TODAY: 'MTPL ends today' },
    coveredUntil: 'MTPL until {date}',
    lastPolicy: 'Last policy {number} ended on {date}',
    nextPolicy: 'Next policy {number} from {date}',
    openPolicy: 'Policy card',
    registryEmpty: 'The policy register is empty — gaps become alarms after the first policy import.',
    noGaps: 'Every active vehicle has valid MTPL today and tomorrow',
    empty: 'No vehicles for this filter',
  },
  contracts: {
    add: 'Add contract',
    addTitle: 'New master contract',
    editTitle: 'Edit master contract',
    empty: 'No master contracts',
    emptyHint: 'A contract is created by importing a file with a contract number, or added manually.',
    broker: 'broker',
    daysLeft: '{n} days left',
    showPolicies: 'Policies ({n})',
    today: 'Today',
    policies: 'Policies',
    vehicles: 'vehicles: {n}',
    premium: 'Premium',
    fromPolicies: 'from policies {amount}',
    paid: 'Paid',
    due: 'Due',
    next: 'next {date}',
    ocOnly: 'MTPL only: {n}',
    ocAc: 'MTPL + own damage: {n}',
    cargo: 'Cargo / carrier: {n}',
    endDate: 'ends {date}: {n}',
    installments: 'Installments',
    noInstallments: 'No installments in this contract.',
    paidOn: 'paid {date}',
    inDays: 'in {n} days',
    markPaid: 'Paid',
    undoPaid: 'Undo',
    markedPaid: 'Installment marked as paid',
    markedUnpaid: 'Installment payment undone',
    confirmDelete: 'Delete contract {number}? Its policies stay in the register without a contract.',
    deleted: 'Contract deleted',
    saved: 'Contract saved',
    addInstallment: 'Add installment',
    installmentsHint: 'Installments of the policies in this contract are summed automatically after each import.',
    fields: { number: 'Contract number', insurer: 'Insurer', broker: 'Broker', title: 'Name', start: 'Start', end: 'End', totalPremium: 'Total premium per contract (PLN)', notes: 'Notes', dueDate: 'Due date', amount: 'Amount' },
  },
  drawer: {
    policy: 'Policy',
    aria: 'Policy {number}',
    period: 'Coverage period',
    premium: 'Premium',
    acSum: 'Own damage sum',
    claims12m: 'Claims 12 mo.',
    contract: 'Master contract',
    vehicle: 'Vehicle',
    noVehicle: 'The vehicle from this policy is not in the fleet — add it or pick a vehicle when editing the policy.',
    scope: 'Cover and sums',
    premiums: 'Premiums and installments',
    installment: 'Installment {n} · {date}',
    claims: 'Vehicle claims',
    claimsPeriod: 'In the policy period',
    claimsPaid: 'paid {amount} · {pct}% of premium',
    downtime: 'downtime: {n} days',
    history: 'Vehicle policy history',
    source: 'Source',
    importFile: 'Import file',
    rawRow: 'Row from the file',
    confirmDelete: 'Delete policy {number} from the register?',
    deleted: 'Policy deleted',
  },
  form: { addTitle: 'New policy', editTitle: 'Edit policy', addHint: 'An individual policy outside an import (e.g. bought during the year).', noVehicle: 'Vehicle outside the fleet', saved: 'Policy saved' },
  documents: { title: 'Documents', empty: 'No documents (policy PDF, terms, annex).', upload: 'Add file', uploaded: 'Document added', download: 'Download', confirmDelete: 'Delete file {name}?' },
  fields: {
    policyNumber: 'Policy no.', insurerName: 'Insurer', startDate: 'Period start', endDate: 'Period end', registrationNo: 'Registration no.', vin: 'VIN',
    vehicleLabel: 'Vehicle description', productionYear: 'Production year', objectKind: 'Vehicle type', ownerName: 'Owner', ownerNip: 'Owner tax ID', leasing: 'Leasing',
    lines: 'Lines', ocSumPerson: 'MTPL — limit (persons)', ocSumProperty: 'MTPL — limit (property)', acSumInsured: 'Own damage sum (PLN)', acSumBasis: 'Own damage sum basis',
    acVariant: 'Own damage variant', acTerritory: 'Own damage territory', acDeductible: 'Own damage deductible (PLN)', acOwnShare: 'Own damage own share', premiumTotal: 'Total premium (PLN)',
    premiumByLine: 'Premium by line', installments: 'Installments', coverage: 'Additional cover', vehicleId: 'Fleet vehicle', contractId: 'Master contract',
  },
  coverageKeys: {
    acValue100: 'Own damage — 100% value', acSumProtection: 'Own damage — sum protection', acTowingParking: 'Towing and parking', acTowingLimit: 'Towing — limit',
    acReplacementLimit: 'Replacement car — limit', acNonStop: 'Auto Non Stop', acRussiaBelarus: 'Theft: Russia, Belarus, Ukraine, Moldova', nnwSum: 'Personal accident — sum',
    cargoSum: 'Cargo — sum insured', pwdVariant: 'Roadside assistance — variant', zkSumPerson: 'Green Card — limit (persons)', zkSumProperty: 'Green Card — limit (property)',
    capacity: 'Engine capacity [cm³]', dmc: 'GVW', ZK: 'Green Card',
  },
  import: {
    title: 'Policy import',
    description: 'Excel file from the insurer or broker → preview of register changes and fleet matching → apply',
    stepsAria: 'Import steps',
    step: 'Step {n}',
    steps: { upload: 'Upload file', preview: 'Review changes', done: 'Done' },
    uploadTitle: 'File from the insurer or broker',
    uploadHint: 'Excel .xlsx — fleet contract annex or a policy list. Save old .xls files as .xlsx.',
    drop: 'Drop the file here or pick it from disk',
    choose: 'Choose file',
    insurerLabel: 'Insurer (if the file does not say)',
    insurerPlaceholder: 'e.g. insurer name',
    insurerHint: 'Broker lists have the insurer in each row — leave it empty then.',
    previewBtn: 'Show preview',
    tips: {
      layouts: 'Column layout is recognised by headers: YES/NO line flags, own damage details, lines in one column.',
      safe: 'The preview saves nothing — the register changes only after you apply.',
      vehicles: 'Policies are matched to fleet vehicles by plate number, or by VIN when the plate is missing.',
    },
    another: 'Import another file',
    meta: '{layout} · rows: {rows} · columns: {columns} · sheet “{sheet}”',
    layout: { FLEET_EXTENDED: 'Fleet annex (extended)', FLEET_OC_AC: 'Fleet annex MTPL + own damage', FLEET_OC: 'Fleet annex MTPL', BROKER_LIST: 'Broker list', GENERIC: 'Other layout' },
    status: { PREVIEW: 'Preview', APPLIED: 'Applied', DISCARDED: 'Discarded' },
    rowStatus: { created: 'New', updated: 'Changed', unchanged: 'Unchanged' },
    sum: {
      created: 'New policies',
      updated: 'Changed',
      unchanged: 'Unchanged',
      removed: 'missing in file: {n}',
      premium: 'Premium in file',
      acSum: 'own damage sum {amount}',
      matched: 'matched to fleet: {n}',
      unmatched: 'no fleet vehicle: {n}',
      withoutPolicy: 'fleet vehicles without a policy: {n}',
      corrections: 'normalisations: {n}',
    },
    noInsurer: 'No insurer — enter it when uploading the file',
    hasErrors: 'The file has {n} errors. Rows with an unreadable date cannot be applied — fix the file and upload it again.',
    apply: 'Apply import',
    discard: 'Discard',
    confirmApply: 'Apply the import of {n} rows? Policies will be saved to the register and fleet insurance dates updated.',
    appliedTitle: 'Import applied',
    appliedSummary: 'New: {created} · changed: {updated} · unchanged: {unchanged} · vehicles updated: {vehicles}',
    discarded: 'Import discarded',
    discardedTitle: 'Import discarded — register unchanged',
    toPolicies: 'Go to policies',
    warningsTitle: 'Warnings and proposals',
    warningsHint: 'Expand to see the rows. Normalised values are saved; the original stays in the row history.',
    rowsAll: 'All rows',
    onlyUnmatched: 'Only without vehicle',
    moreRows: 'Remaining rows: {n} (visible in the policy list after applying)',
    rowNo: 'row {n}',
    history: 'Import history',
    historyEmpty: 'No imports yet.',
    historyCounts: 'rows: {rows} · new: {created} · changed: {updated}',
    warning: {
      MODEL_NO_BRAND: { title: 'Model without make', hint: 'The make is added from the model name (e.g. Sprinter → Mercedes).' },
      NUMERIC_DATE: { title: 'Date stored as a number', hint: 'Read as an Excel date.' },
      EMPTY_COLUMN: { title: 'Empty columns', hint: 'A column with a header but no values.' },
      BAD_AMOUNT: { title: 'Unreadable amounts', hint: 'The amount is not a number — the field stays empty.' },
      BAD_DATE: { title: 'Unreadable period date', hint: 'The import cannot be applied without period dates.' },
      MISSING_REGISTRATION: { title: 'No registration number', hint: 'The vehicle is matched by VIN if present.' },
      DUPLICATE_POLICY: { title: 'Duplicated policy', hint: 'Same number and period appear more than once — the last row wins.' },
      UNKNOWN_COLUMNS: { title: 'Unrecognised columns', hint: 'Kept in the policy row history.' },
      TOTAL_MISMATCH: { title: 'Total premium ≠ sum of line premiums', hint: 'The total premium from the file is saved.' },
      NO_LINES: { title: 'No lines', hint: 'No insurance line recognised — check the cover columns.' },
      NO_VEHICLE: { title: 'Vehicle not in the fleet', hint: 'The policy is saved without a vehicle — add the vehicle or link it later.' },
      MATCHED_BY_VIN: { title: 'Matched by VIN', hint: 'The plate number in the fleet differs from the policy.' },
      VIN_MISMATCH: { title: 'VIN differs from the fleet', hint: 'Check it is the same vehicle.' },
      VEHICLE_WITHOUT_POLICY: { title: 'Fleet vehicles without a policy', hint: 'Active vehicles of this type without MTPL in the register or the file — alarm.' },
      MISSING_IN_FILE: { title: 'Missing in the file', hint: 'Register policies of this contract that are not in the file — left unchanged.' },
    },
  },
  vehicleCard: {
    tab: 'Insurance',
    title: 'Vehicle coverage',
    covered: 'Valid MTPL today and tomorrow',
    noCoverToday: 'No valid MTPL today',
    endsToday: 'Coverage ends today',
    required: 'Required: motor third-party liability (MTPL)',
    openPolicy: 'Policy card',
    insuranceDue: 'Date in fleet',
    upcoming: 'Next policy {number} ({insurer}) from {date}',
    noPolicy: 'No policy for this vehicle in the register',
    noPolicyHint: 'Import the insurer file or add the policy manually.',
    claims: 'Vehicle claims',
    claims12m: 'Claims 12 mo.',
    paid12m: 'Paid 12 mo.',
    lossRatio: '{pct}% of premium',
  },
  errors: {
    network: 'No connection to the server.',
    forbidden: 'You are not allowed to do this.',
    generic: 'Something went wrong. Please try again.',
    POLICY_NOT_FOUND: 'Policy not found.',
    POLICY_VEHICLE_NOT_FOUND: 'Fleet vehicle not found.',
    POLICY_REQUIRED_FIELDS: 'Enter the policy number, insurer, period and at least one line.',
    POLICY_BAD_PERIOD: 'The period end is before the start.',
    POLICY_DUPLICATE: 'A policy with this number and start date already exists.',
    CONTRACT_NOT_FOUND: 'Contract not found.',
    CONTRACT_REQUIRED_FIELDS: 'Enter the contract number, insurer and period.',
    CONTRACT_BAD_PERIOD: 'The contract end is before the start.',
    CONTRACT_DUPLICATE: 'A contract with this number already exists.',
    INSTALLMENT_NOT_FOUND: 'Installment not found.',
    VEHICLE_NOT_FOUND: 'Vehicle not found.',
    IMPORT_FILE_REQUIRED: 'Choose a file.',
    IMPORT_XLS_UNSUPPORTED: 'Old .xls format — save the file as .xlsx in Excel and upload again.',
    IMPORT_NOT_EXCEL: 'This is not an Excel .xlsx file.',
    IMPORT_NO_SHEET: 'The workbook has no sheets.',
    IMPORT_LAYOUT_UNKNOWN: 'Layout not recognised: no header row with a “Policy no.” column.',
    IMPORT_MISSING_COLUMNS: 'Missing columns: policy number, plate / VIN or insurance period (from / to).',
    IMPORT_NO_ROWS: 'The file has no rows with a policy number.',
    IMPORT_NOT_FOUND: 'Import not found.',
    IMPORT_ALREADY_DECIDED: 'This import was already applied or discarded.',
    IMPORT_BAD_PERIODS: 'Some rows have no readable insurance period — fix the file and upload it again.',
    IMPORT_INSURER_REQUIRED: 'No insurer — upload the file again and enter the insurer.',
    ATTACHMENT_OWNER_NOT_FOUND: 'Document not found.',
    FILE_REQUIRED: 'Choose a file.',
    FILE_TYPE_NOT_ALLOWED: 'File type not allowed.',
  },
};
